import {
  ActionReducerMapBuilder,
  AsyncThunk,
  EntityState,
  PayloadAction,
  createEntityAdapter,
  createSlice,
} from "@reduxjs/toolkit";
import {
  CATALOG_IMPORT_PAGE_SIZE,
  CatalogCrop,
  CatalogDraft,
  CatalogDraftPage,
  CatalogImport,
  CatalogImportConfig,
  CatalogImportRejection,
  CatalogImportStatusFilter,
  CatalogPage,
  isImportActive,
  matchesCatalogImportFilter,
} from "../../interfaces/CatalogImportObject";
import { statusChangesAfterBulk } from "../../services/helpers/CatalogImportDraftLogic";
import {
  archiveCatalogImport,
  bulkCatalogDraftAction,
  cancelCatalogImport,
  commitCatalogImport,
  createCatalogCrop,
  createCatalogImport,
  fetchCatalogDrafts,
  fetchCatalogImport,
  fetchCatalogImportConfig,
  fetchCatalogImports,
  fetchCatalogPage,
  fetchMoreCatalogImports,
  mergeCatalogDrafts,
  patchCatalogDraft,
  requestCatalogUploadUrl,
  rerunCatalogPage,
  retryCatalogFailedPages,
  saveCatalogSectionMaps,
  splitCatalogDraft,
  startCatalogImport,
} from "../../services/thunks/CatalogImportThunk";
import {
  CATALOG_IMPORT_CONFIG_KEY,
  CATALOG_IMPORT_CREATE_KEY,
  CATALOG_IMPORT_LIST_KEY,
  CATALOG_IMPORT_MORE_KEY,
  catalogBulkActionKey,
  catalogCropKey,
  catalogDraftKey,
  catalogDraftListCacheKey,
  catalogDraftListKey,
  catalogImportActionKey,
  catalogImportDetailKey,
  catalogMergeKey,
  catalogPageCacheKey,
  catalogPageKey,
  catalogRerunKey,
} from "./CatalogImportKeys";
import { resetAllState } from "./resetAllState";
import { AsyncStatusState, createAsyncStatusState } from "./shared/registerAsync";

export * from "./CatalogImportKeys";

/**
 * State for the catalogue PDF import — the curator's Tasks tab list, the
 * processing view and the review workspace.
 *
 * Shape:
 *  - `importsById` + `listIds` (order kept apart from the entities, so a detail
 *    fetch or an action result updates the card in place without reordering);
 *  - `pagesByKey` (`importId:pageNo`) holds a page WITHOUT its drafts — the
 *    drafts live once, in `drafts` (an entity adapter keyed by draftId) and the
 *    page keeps `draftIds`;
 *  - `cropsById` for every crop seen on a fetched page;
 *  - `draftLists` for the paged product list (`GET .../drafts`);
 *  - loadingMap / errorMap / successMap as everywhere, plus `errorCodeMap`
 *    (the error code behind each errorMap message: "16215", "HTTP_503", ...).
 */

/** A page as cached: the page minus its drafts, plus their ids. */
export type CatalogPageEntry = Omit<CatalogPage, "drafts"> & { draftIds: string[] };

export interface CatalogDraftListEntry {
  ids: string[];
  nextCursor: string | null;
  hasLoaded: boolean;
}

export interface CatalogImportState extends AsyncStatusState {
  /** Limits and the feature switch; null until GET /config answered. */
  config: CatalogImportConfig | null;
  importsById: Record<string, CatalogImport>;
  /** The rows of the current list, newest first. */
  listIds: string[];
  /** The filter `listIds` belongs to; set when a fetch STARTS (see fetchCatalogImports.pending). */
  filter: CatalogImportStatusFilter;
  hasMore: boolean;
  /** True once the first page of the list has landed. */
  hasLoaded: boolean;
  pagesByKey: Record<string, CatalogPageEntry>;
  cropsById: Record<string, CatalogCrop>;
  drafts: EntityState<CatalogDraft, string>;
  draftLists: Record<string, CatalogDraftListEntry>;
  /** Error code behind each errorMap entry. */
  errorCodeMap: Record<string, string | null>;
}

export const catalogDraftAdapter = createEntityAdapter<CatalogDraft, string>({
  selectId: (draft) => draft.draftId,
});

const initialState = (): CatalogImportState => ({
  config: null,
  importsById: {},
  listIds: [],
  filter: "IN_PROGRESS",
  hasMore: false,
  hasLoaded: false,
  pagesByKey: {},
  cropsById: {},
  drafts: catalogDraftAdapter.getInitialState(),
  draftLists: {},
  errorCodeMap: {},
  ...createAsyncStatusState(),
});

// ── Reducer helpers ──────────────────────────────────────────────────────────

/**
 * A fresher copy of an import. List rows carry no `pages` / `sectionMaps`
 * (null) — keep what the detail fetch loaded rather than blanking it.
 */
function mergeImport(previous: CatalogImport | undefined, next: CatalogImport): CatalogImport {
  if (!previous) return next;
  return {
    ...next,
    pages: next.pages ?? previous.pages,
    sectionMaps: next.sectionMaps ?? previous.sectionMaps,
  };
}

function upsertImports(state: CatalogImportState, rows: CatalogImport[]): void {
  rows.forEach((row) => {
    state.importsById[row.importId] = mergeImport(state.importsById[row.importId], row);
  });
}

function upsertDraftEntities(state: CatalogImportState, drafts: CatalogDraft[]): void {
  catalogDraftAdapter.setMany(state.drafts, drafts);
}

/**
 * After a merge or split: forget the drafts that no longer exist, and make sure
 * every resulting draft is listed on each page it appears on.
 */
function reconcileDraftChange(
  state: CatalogImportState,
  importId: string,
  removedIds: readonly string[],
  drafts: CatalogDraft[]
): void {
  const keep = new Set(drafts.map((d) => d.draftId));
  const gone = new Set(removedIds.filter((id) => !keep.has(id)));
  upsertDraftEntities(state, drafts);
  if (gone.size > 0) catalogDraftAdapter.removeMany(state.drafts, Array.from(gone));
  const prefix = `${importId}:`;
  Object.keys(state.pagesByKey).forEach((key) => {
    if (!key.startsWith(prefix)) return;
    const entry = state.pagesByKey[key];
    if (gone.size > 0) entry.draftIds = entry.draftIds.filter((id) => !gone.has(id));
    drafts.forEach((d) => {
      if (d.pageNos.includes(entry.pageNo) && !entry.draftIds.includes(d.draftId)) entry.draftIds.push(d.draftId);
    });
  });
  if (gone.size > 0) {
    Object.values(state.draftLists).forEach((list) => {
      list.ids = list.ids.filter((id) => !gone.has(id));
    });
  }
}

function storePage(state: CatalogImportState, importId: string, page: CatalogPage): void {
  const key = catalogPageCacheKey(importId, page.pageNo);
  const { drafts, ...rest } = page;
  state.pagesByKey[key] = { ...rest, draftIds: drafts.map((d) => d.draftId) };
  upsertDraftEntities(state, drafts);
  page.crops.forEach((crop) => {
    state.cropsById[crop.cropId] = crop;
  });
}

/** Imports leave the list view they were in only through a refresh, never mid-action. */
function listIdsStillMatching(state: CatalogImportState, ids: string[]): string[] {
  return ids.filter((id) => {
    const row = state.importsById[id];
    return !!row && matchesCatalogImportFilter(state.filter, row.status);
  });
}

type Rejection = CatalogImportRejection | undefined;

interface RegisterConfig<Returned, Arg> {
  key: string | ((arg: Arg) => string);
  loading?: string;
  /** Skip loading / error bookkeeping entirely (silent polling). */
  silent?: (arg: Arg) => boolean;
  onPending?: (state: CatalogImportState, arg: Arg) => void;
  onFulfilled?: (state: CatalogImportState, payload: Returned, arg: Arg) => void;
  /** Return true when the rejection was handled as a state, not a failure. */
  onRejected?: (state: CatalogImportState, rejection: CatalogImportRejection, arg: Arg) => boolean | void;
}

const FALLBACK_REJECTION: CatalogImportRejection = {
  message: "Something went wrong. Please try again.",
  code: "CLIENT_ERROR",
  httpStatus: 0,
  retryable: true,
  existingImportId: null,
  details: null,
};

/**
 * registerAsync's counterpart for thunks that reject with a
 * CatalogImportRejection: same loadingMap / errorMap / successMap bookkeeping,
 * plus errorCodeMap. (registerAsync pins `rejectValue: string`.)
 */
function register<Returned, Arg>(
  builder: ActionReducerMapBuilder<CatalogImportState>,
  thunk: AsyncThunk<Returned, Arg, { rejectValue: CatalogImportRejection }>,
  config: RegisterConfig<Returned, Arg>
): void {
  const keyOf = (arg: Arg): string => (typeof config.key === "function" ? config.key(arg) : config.key);

  builder
    .addCase(thunk.pending, (state, action) => {
      const s = state as unknown as CatalogImportState;
      const arg = action.meta.arg as Arg;
      config.onPending?.(s, arg);
      if (config.silent?.(arg)) return;
      const key = keyOf(arg);
      s.loadingMap[key] = config.loading ?? "Loading...";
      s.errorMap[key] = null;
      s.errorCodeMap[key] = null;
      s.successMap[key] = false;
    })
    .addCase(thunk.fulfilled, (state, action) => {
      const s = state as unknown as CatalogImportState;
      const arg = action.meta.arg as Arg;
      if (!config.silent?.(arg)) {
        const key = keyOf(arg);
        s.loadingMap[key] = null;
        s.successMap[key] = true;
        s.errorMap[key] = null;
        s.errorCodeMap[key] = null;
      }
      config.onFulfilled?.(s, action.payload as Returned, arg);
    })
    .addCase(thunk.rejected, (state, action) => {
      const s = state as unknown as CatalogImportState;
      const arg = action.meta.arg as Arg;
      const rejection = ((action.payload as Rejection) ?? {
        ...FALLBACK_REJECTION,
        message: action.error?.message || FALLBACK_REJECTION.message,
      }) as CatalogImportRejection;
      // A silent refresh that failed must not wipe what is on screen or flash
      // an error over it; the next tick tries again.
      if (config.silent?.(arg)) return;
      const key = keyOf(arg);
      s.loadingMap[key] = null;
      // A request the caller abandoned on purpose (the review switched pages)
      // is not a failure: no error text for a page nobody is looking at.
      if (action.meta.aborted) return;
      const handled = config.onRejected?.(s, rejection, arg);
      if (!handled) {
        s.errorMap[key] = rejection.message;
        s.errorCodeMap[key] = rejection.code;
      }
    });
}

// ── Slice ────────────────────────────────────────────────────────────────────

const catalogImportSlice = createSlice({
  name: "catalogImport",
  initialState,
  reducers: {
    /** Puts an import the caller already holds into the cache (no request). */
    catalogImportReceived(state, action: PayloadAction<CatalogImport>) {
      upsertImports(state, [action.payload]);
    },
    /** Drops one errorMap entry (e.g. when a dialog closes). */
    catalogImportErrorCleared(state, action: PayloadAction<string>) {
      state.errorMap[action.payload] = null;
      state.errorCodeMap[action.payload] = null;
    },
  },
  extraReducers: (builder) => {
    // fetchCatalogImportConfig
    register(builder, fetchCatalogImportConfig, {
      key: CATALOG_IMPORT_CONFIG_KEY,
      loading: "Loading limits...",
      onFulfilled: (state, config) => {
        state.config = config;
      },
    });

    // fetchCatalogImports — the list. `pending` records the filter so the chips
    // move immediately and a late response for an older filter is recognisable.
    // A silent refresh (polling) merges instead of replacing: rows below page 1
    // that "Load more" fetched stay, rows that left the filter drop out.
    register(builder, fetchCatalogImports, {
      key: CATALOG_IMPORT_LIST_KEY,
      loading: "Loading imports...",
      silent: (query) => !!query.silent,
      onPending: (state, query) => {
        if (query.silent) return;
        state.filter = query.filter;
        state.errorMap[CATALOG_IMPORT_MORE_KEY] = null;
      },
      onFulfilled: (state, rows, query) => {
        if (query.filter !== state.filter) return;
        upsertImports(state, rows);
        const ids = rows.map((row) => row.importId);
        if (query.silent) {
          const known = new Set(ids);
          const tail = listIdsStillMatching(
            state,
            state.listIds.filter((id) => !known.has(id))
          );
          state.listIds = [...ids, ...tail];
          if (tail.length === 0) state.hasMore = rows.length >= CATALOG_IMPORT_PAGE_SIZE;
        } else {
          state.listIds = ids;
          state.hasMore = rows.length >= CATALOG_IMPORT_PAGE_SIZE;
        }
        state.hasLoaded = true;
      },
      onRejected: (state, rejection, query) => {
        if (query.filter !== state.filter) return true;
        state.errorMap[CATALOG_IMPORT_LIST_KEY] = rejection.message;
        state.errorCodeMap[CATALOG_IMPORT_LIST_KEY] = rejection.code;
        return true;
      },
    });

    // fetchMoreCatalogImports — appended, de-duplicated: a refresh that lands
    // while this is in flight can already hold some of the same rows.
    register(builder, fetchMoreCatalogImports, {
      key: CATALOG_IMPORT_MORE_KEY,
      loading: "Loading more...",
      onFulfilled: (state, rows, query) => {
        if (query.filter !== state.filter) return;
        upsertImports(state, rows);
        const known = new Set(state.listIds);
        state.listIds.push(...rows.map((r) => r.importId).filter((id) => !known.has(id)));
        state.hasMore = rows.length >= CATALOG_IMPORT_PAGE_SIZE;
      },
    });

    // fetchCatalogImport — detail (adds pages + section maps); also refreshes the list row.
    register(builder, fetchCatalogImport, {
      key: ({ importId }) => catalogImportDetailKey(importId),
      loading: "Loading import...",
      silent: ({ silent }) => !!silent,
      onFulfilled: (state, row) => upsertImports(state, [row]),
    });

    // Upload flow. All three share the create key: one upload runs at a time.
    register(builder, createCatalogImport, {
      key: CATALOG_IMPORT_CREATE_KEY,
      loading: "Preparing upload...",
      onFulfilled: (state, result) => {
        const row = result.importJob;
        if (!row.importId) return;
        upsertImports(state, [row]);
        // Shown at the top of the list if it belongs to the current filter;
        // otherwise it appears when the user switches to one it matches.
        if (
          state.hasLoaded &&
          matchesCatalogImportFilter(state.filter, row.status) &&
          !state.listIds.includes(row.importId)
        ) {
          state.listIds.unshift(row.importId);
        }
      },
    });
    register(builder, requestCatalogUploadUrl, {
      key: CATALOG_IMPORT_CREATE_KEY,
      loading: "Preparing upload...",
      onFulfilled: (state, result) => {
        if (result.importJob.importId) upsertImports(state, [result.importJob]);
      },
    });
    register(builder, startCatalogImport, {
      key: CATALOG_IMPORT_CREATE_KEY,
      loading: "Starting import...",
      onFulfilled: (state, row) => upsertImports(state, [row]),
    });

    // Import actions — one per-row "busy" key, result swapped in place.
    const importAction = (
      thunk: AsyncThunk<CatalogImport, string, { rejectValue: CatalogImportRejection }>,
      loading: string
    ) =>
      register(builder, thunk, {
        key: (importId) => catalogImportActionKey(importId),
        loading,
        onFulfilled: (state, row) => upsertImports(state, [row]),
      });
    importAction(cancelCatalogImport, "Cancelling...");
    importAction(retryCatalogFailedPages, "Retrying pages...");
    importAction(archiveCatalogImport, "Archiving...");

    register(builder, commitCatalogImport, {
      key: ({ importId }) => catalogImportActionKey(importId),
      loading: "Creating products...",
      onFulfilled: (state, row) => upsertImports(state, [row]),
    });
    register(builder, saveCatalogSectionMaps, {
      key: ({ importId }) => catalogImportActionKey(importId),
      loading: "Saving sections...",
      onFulfilled: (state, row) => upsertImports(state, [row]),
    });

    // Review: pages, drafts.
    register(builder, fetchCatalogPage, {
      key: ({ importId, pageNo }) => catalogPageKey(importId, pageNo),
      loading: "Loading page...",
      silent: ({ silent }) => !!silent,
      onFulfilled: (state, page, { importId }) => storePage(state, importId, page),
    });

    register(builder, fetchCatalogDrafts, {
      key: ({ importId }) => catalogDraftListKey(importId),
      loading: "Loading products...",
      onFulfilled: (state, result: CatalogDraftPage, query) => {
        upsertDraftEntities(state, result.drafts);
        const cacheKey = catalogDraftListCacheKey(query.importId, query.status, query.flag, query.pageNo);
        const previous = query.cursor ? state.draftLists[cacheKey]?.ids ?? [] : [];
        const known = new Set(previous);
        state.draftLists[cacheKey] = {
          ids: [...previous, ...result.drafts.map((d) => d.draftId).filter((id) => !known.has(id))],
          nextCursor: result.nextCursor,
          hasLoaded: true,
        };
      },
    });

    register(builder, patchCatalogDraft, {
      key: ({ draftId }) => catalogDraftKey(draftId),
      loading: "Saving...",
      onFulfilled: (state, draft) => upsertDraftEntities(state, [draft]),
    });

    // Bulk actions return counts only: apply the statuses locally to the cached
    // drafts (minus `skipped`) and take the refreshed counters.
    register(builder, bulkCatalogDraftAction, {
      key: ({ importId }) => catalogBulkActionKey(importId),
      loading: "Updating products...",
      onFulfilled: (state, result, { importId, form }) => {
        const cached = catalogDraftAdapter
          .getSelectors()
          .selectAll(state.drafts)
          .filter((d) => d.importId === importId);
        const changes = statusChangesAfterBulk(form, cached, result.skipped);
        catalogDraftAdapter.updateMany(
          state.drafts,
          changes.map((c) => ({ id: c.draftId, changes: { status: c.to } }))
        );
        const row = state.importsById[importId];
        if (row && result.progress) row.progress = result.progress;
      },
    });

    register(builder, mergeCatalogDrafts, {
      key: ({ importId }) => catalogMergeKey(importId),
      loading: "Combining products...",
      onFulfilled: (state, drafts, { importId, form }) =>
        reconcileDraftChange(state, importId, form.draftIds, drafts),
    });
    register(builder, splitCatalogDraft, {
      key: ({ importId }) => catalogMergeKey(importId),
      loading: "Splitting product...",
      onFulfilled: (state, drafts, { importId }) => reconcileDraftChange(state, importId, [], drafts),
    });

    register(builder, createCatalogCrop, {
      key: ({ importId, pageNo }) => catalogCropKey(importId, pageNo),
      loading: "Cutting image...",
      onFulfilled: (state, result, { importId, pageNo }) => {
        const entry = state.pagesByKey[catalogPageCacheKey(importId, pageNo)];
        if (result.crop) {
          state.cropsById[result.crop.cropId] = result.crop;
          if (entry && !entry.crops.some((c) => c.cropId === result.crop?.cropId)) {
            entry.crops.push(result.crop);
          }
        }
        if (result.draft) {
          upsertDraftEntities(state, [result.draft]);
          if (entry && !entry.draftIds.includes(result.draft.draftId)) {
            entry.draftIds.push(result.draft.draftId);
          }
        }
      },
    });

    // Re-run replaces the page's unapproved drafts: forget the ones that vanished.
    register(builder, rerunCatalogPage, {
      key: ({ importId, pageNo }) => catalogRerunKey(importId, pageNo),
      loading: "Reading the page again...",
      onFulfilled: (state, page, { importId, pageNo }) => {
        const cacheKey = catalogPageCacheKey(importId, pageNo);
        const before = state.pagesByKey[cacheKey]?.draftIds ?? [];
        const now = new Set(page.drafts.map((d) => d.draftId));
        const gone = before.filter((id) => {
          if (now.has(id)) return false;
          const status = state.drafts.entities[id]?.status;
          return status === "PROPOSED" || status === "NEEDS_ATTENTION";
        });
        if (gone.length > 0) catalogDraftAdapter.removeMany(state.drafts, gone);
        storePage(state, importId, page);
      },
    });

    builder.addCase(resetAllState, () => initialState());
  },
});

export const { catalogImportReceived, catalogImportErrorCleared } = catalogImportSlice.actions;
export default catalogImportSlice.reducer;

// ── Selectors ────────────────────────────────────────────────────────────────
// Plain functions of `{ catalogImport }` so they work with useSelector and tests
// without importing the store (RootState lives in UiDb.ts — importing it here
// would recreate the cycle the keys file exists to avoid).

type StoreSlice = { catalogImport: CatalogImportState };

export const selectCatalogImportConfig = (state: StoreSlice): CatalogImportConfig | null =>
  state.catalogImport.config;

export const selectCatalogImport = (state: StoreSlice, importId: string): CatalogImport | undefined =>
  state.catalogImport.importsById[importId];

export const selectCatalogPageEntry = (
  state: StoreSlice,
  importId: string,
  pageNo: number
): CatalogPageEntry | undefined => state.catalogImport.pagesByKey[catalogPageCacheKey(importId, pageNo)];

export const selectCatalogDraft = (state: StoreSlice, draftId: string): CatalogDraft | undefined =>
  state.catalogImport.drafts.entities[draftId];

export const selectCatalogCrop = (state: StoreSlice, cropId: string): CatalogCrop | undefined =>
  state.catalogImport.cropsById[cropId];

export const selectCatalogImportError = (
  state: StoreSlice,
  key: string
): { message: string | null; code: string | null } => ({
  message: state.catalogImport.errorMap[key] ?? null,
  code: state.catalogImport.errorCodeMap[key] ?? null,
});

/** True when any of the listed imports is changing on the server by itself — what the list poller watches. */
export const selectCatalogListHasActiveImports = (state: StoreSlice): boolean =>
  state.catalogImport.listIds.some((id) => {
    const status = state.catalogImport.importsById[id]?.status;
    return status !== undefined && isImportActive(status);
  });
